import { useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { getComicById, getRelatedComics, comicCategories } from "@/mocks/comicData";

function ReadingView({ comic }: { comic: ReturnType<typeof getComicById> }) {
  const [searchParams] = useSearchParams();
  const chapterNum = searchParams.get("chapter");
  const selectedChapter = chapterNum
    ? comic?.chapters.find((ch) => String(ch.number) === chapterNum)
    : comic?.chapters[0];

  if (!comic || !selectedChapter) return null;

  const readingContent = `　　${comic.description}

　　第${selectedChapter.number}话 · ${selectedChapter.title.replace(`第${selectedChapter.number}话 - `, "")}

　　【本话内容为下拉式阅读体验演示】

　　清晨的阳光透过窗帘洒进房间，柔和的光线在地板上投下斑驳的影子。她缓缓睁开眼睛，望着天花板上熟悉的花纹，脑海中却回荡着昨晚那个不可思议的约定。

　　"你确定要这么做吗？"她轻声问自己，手指无意识地摩挲着被角的边缘。

　　公寓楼下传来汽车启动的声音，打破了清晨的宁静。她起身走到窗边，看着街道上开始忙碌的人群。这座城市从不休息，就像她心底那份无法抑制的感情一样。

　　手机震动了。

　　屏幕上显示着一个再熟悉不过的名字。她的手停在半空中，犹豫了三秒钟，最终还是按下了接听键。

　　"喂……"

　　"我在楼下。"

　　简单的四个字，却让她的心跳骤然加速。她放下手机，对着镜子整理了一下头发，深呼吸，然后推开了房门。

　　电梯里只有她一个人。数字一层层下降，每一层的跳动都像是在为某个重要时刻倒计时。叮——电梯门打开的瞬间，她看到了那个倚靠在车门旁的身影。

　　晨光勾勒出他挺拔的轮廓，手中还拎着两杯冒着热气的咖啡。他抬起头，目光与她相遇的刹那，嘴角微微上扬。

　　"早啊。"他把其中一杯递给她，"热美式，不加糖，对吧？"

　　她接过咖啡，指尖不小心触碰到他的手背，一阵电流般的感觉窜过全身。"你……怎么知道？"

　　"上次开会的时候，你喝了三杯。"他的语气轻描淡写，眼神却藏着温柔。

　　她低头喝了一口咖啡，用杯沿掩饰自己泛红的脸颊。咖啡的苦味在舌尖化开，但心里却甜得像糖融化了一般。

　　"所以，"他率先打破沉默，"昨晚说的事情，你考虑好了吗？"

　　她抬起头，正对上他认真的目光。风恰好吹过，撩起她耳边的碎发，也似乎带走了最后一丝犹豫。

　　"我……"她握紧了咖啡杯，感受着掌心传来的温度，"我想好了。"

　　她顿了顿，然后说完了剩下的话。

　　他听完后，眼中的紧张逐渐化开，取而代之的是明亮的笑意。"那我们现在就去。"

　　"现在？！"

　　"不然呢？"他已经拉开了车门，"你该不会以为我在开玩笑吧？"

　　她站在原地，看着他那双真诚的眼睛，忽然觉得一切的顾虑都变得不再重要了。她笑了，然后坐进了车里。

　　引擎发动的声音在清晨的街道上格外清晰。车子缓缓驶出小区，融入城市川流不息的车流中。

　　而她不知道的是，从这一刻起，命运的齿轮已经开始转动，一切都将变得不同……

　　—— 下拉继续阅读 ——

　　午后的阳光变得慵懒而温暖。他们并肩走在江边的步道上，远处的高楼在薄雾中若隐若现。

　　"你知道吗，"他忽然停下脚步，双手撑在栏杆上，望向远方，"我从来没有想过，会有人让我如此在意。"

　　她站在他身旁，看着他侧脸的轮廓。阳光在他的睫毛上投下金色的光晕，让她不由自主地屏住了呼吸。

　　"我也是。"她的声音轻得像风中的叹息。

　　他转过头，两人四目相对。在这个瞬间，整个世界仿佛都安静了下来，只剩下江水拍打岸边的声音，以及彼此越来越清晰的心跳。

　　"给我一个机会，"他说，"让我证明给你看。"

　　她没有回答，但她伸出了手，轻轻握住了他的指尖。

　　不需要言语。答案已经写在了交握的双手之间，写在了这个温暖而美好的午后。

　　江风吹过，带走了所有的忐忑与不安，只留下两颗渐渐靠近的心，在这个城市最普通的角落里，发生着最不普通的奇迹。`;

  return (
    <div className="max-w-[720px] mx-auto px-4 md:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <Link
          to={`/article/${comic.id}`}
          className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          返回详情
        </Link>
        <div className="flex items-center gap-2 text-sm text-foreground-500">
          <span>{selectedChapter.title}</span>
        </div>
        <div className="flex items-center gap-2">
          {comic.chapters.findIndex((ch) => ch.id === selectedChapter.id) > 0 && (
            <Link
              to={`/article/${comic.id}?read=1&chapter=${selectedChapter.number - 1}`}
              className="flex items-center gap-1 px-3 py-1.5 bg-foreground-950 text-white rounded-full text-xs font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-up-line w-3 h-3 flex items-center justify-center"></i>
              上一话
            </Link>
          )}
          {comic.chapters.findIndex((ch) => ch.id === selectedChapter.id) < comic.chapters.length - 1 && (
            <Link
              to={`/article/${comic.id}?read=1&chapter=${selectedChapter.number + 1}`}
              className="flex items-center gap-1 px-3 py-1.5 bg-foreground-950 text-white rounded-full text-xs font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              下一话
              <i className="ri-arrow-down-line w-3 h-3 flex items-center justify-center"></i>
            </Link>
          )}
        </div>
      </div>

      <article className="bg-white rounded-2xl border border-background-200 p-6 md:p-10">
        <h1 className="text-xl font-bold text-foreground-950 mb-2 font-heading">
          {comic.title}
        </h1>
        <p className="text-sm text-foreground-500 mb-6">
          {selectedChapter.title}
        </p>
        <div className="prose prose-sm max-w-none text-foreground-800 leading-loose whitespace-pre-line">
          {readingContent}
        </div>
      </article>

      <div className="flex items-center justify-center gap-2 mt-8">
        {comic.chapters.findIndex((ch) => ch.id === selectedChapter.id) > 0 && (
          <Link
            to={`/article/${comic.id}?read=1&chapter=${selectedChapter.number - 1}`}
            className="flex items-center gap-1.5 px-5 py-3 bg-foreground-950 text-white rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            上一话
          </Link>
        )}
        <Link
          to={`/article/${comic.id}`}
          className="flex items-center gap-1.5 px-5 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-list-check w-4 h-4 flex items-center justify-center"></i>
          章节列表
        </Link>
        {comic.chapters.findIndex((ch) => ch.id === selectedChapter.id) < comic.chapters.length - 1 && (
          <Link
            to={`/article/${comic.id}?read=1&chapter=${selectedChapter.number + 1}`}
            className="flex items-center gap-1.5 px-5 py-3 bg-foreground-950 text-white rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            下一话
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        )}
      </div>
    </div>
  );
}

function DetailView({ comic }: { comic: ReturnType<typeof getComicById> }) {
  if (!comic) return null;

  const related = getRelatedComics(comic, 5);

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 pb-16">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:w-[280px] flex-shrink-0">
          <div className="sticky top-24">
            <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-100">
              <img
                src={comic.coverImage}
                alt={comic.title}
                title={`${comic.title} - 韩漫免费漫画在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-4 space-y-2">
              <Link
                to={`/article/${comic.id}?read=1`}
                className="block w-full text-center py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line mr-1.5"></i>
                开始阅读
              </Link>
              <button className="w-full text-center py-2.5 bg-background-100 text-foreground-700 rounded-xl text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-bookmark-line mr-1.5"></i>
                加入收藏
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">
            {comic.title}
          </h1>

          <div className="flex flex-wrap items-center gap-3 mb-5 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              {comic.author}
            </span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-400"></span>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-yellow-400 w-4 h-4 flex items-center justify-center"></i>
              {comic.rating}
            </span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-400"></span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
              {(comic.totalViews / 10000).toFixed(0)}万
            </span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-400"></span>
            <span>共{comic.totalChapters}话</span>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {comic.categories.map((catSlug) => {
              const cat = comicCategories.find((c) => c.slug === catSlug);
              return cat ? (
                <Link
                  key={catSlug}
                  to={`/category/${catSlug}`}
                  className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {cat.name}
                </Link>
              ) : null;
            })}
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                comic.status === "已完结"
                  ? "bg-accent-100 text-accent-700"
                  : "bg-secondary-100 text-secondary-700"
              }`}
            >
              {comic.status}
            </span>
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5 mb-8">
            <h2 className="text-base font-semibold text-foreground-950 mb-3">
              作品简介
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {comic.description}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-foreground-950">
                章节列表
              </h2>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-lg text-xs font-medium whitespace-nowrap">
                  全部章节
                </span>
              </div>
            </div>
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/article/${comic.id}?read=1&chapter=${chapter.number}`}
                  className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <span className="text-sm text-foreground-800 font-medium">
                    {chapter.title}
                  </span>
                  <div className="flex items-center gap-4 text-xs text-foreground-400">
                    <span>{chapter.views.toLocaleString()}次</span>
                    <span>{chapter.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-14">
          <h2 className="text-xl font-bold text-foreground-950 mb-6 font-heading">
            相关推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} size="small" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isReading = searchParams.get("read") === "1";

  const comic = useMemo(() => (slug ? getComicById(slug) : undefined), [slug]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <main className="min-h-screen bg-background-50 pt-20">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">
              漫画未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              您要查看的漫画不存在或已被下架
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: comic.title,
            author: { "@type": "Person", name: comic.author },
            description: comic.description,
            datePublished: comic.chapters[comic.chapters.length - 1]?.date,
            dateModified: comic.updatedAt,
            image: comic.coverImage,
            publisher: {
              "@type": "Organization",
              name: "虚空韩漫网",
            },
          }),
        }}
      />
      <main className="min-h-screen bg-background-50">
        {!isReading && (
          <div className="pt-20 pb-6">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              <Breadcrumb />
            </div>
          </div>
        )}

        {isReading ? (
          <div className="pt-20">
            <ReadingView comic={comic} />
          </div>
        ) : (
          <DetailView comic={comic} />
        )}
      </main>
    </>
  );
}